/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Effect, MaterialTechTransfer, Nullable } from "../../utils/Interfaces";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";

import { ArrowForward, CancelScheduleSend, Check, Close, Send } from "@mui/icons-material";
import { useWindowDimensions } from "../../utils/Hooks";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { createMaterialTechTransfer, updatePendingMaterials, updateReceivingMaterials } from "./materialsTechTransferFuctions";
import InventoryTechTransferModal from "./InventoryTechTransferModal";
import CancelMaterialTechTransferModal from "./CancelMaterialTechTransferModal";
import AcceptMaterialTechTransferModal from "./AcceptMaterialTechTransferModal";

const MaterialsTechTransfers = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();

    let mobile: boolean = width <= 600;

    const [materialsToReceive, setMaterialsToReceive] = useState<MaterialTechTransfer[]>([]);
    const [materialsPending, setMaterialsPending] = useState<MaterialTechTransfer[]>([]);

    const [selectedMaterialTransfer, setSelectedMaterialTransfer] = useState<Nullable<MaterialTechTransfer>>(null);

    const [initiateMaterialTransferModalOpen, setInitiateMaterialTransferModalOpen] = useState<boolean>(false);

    const [cancelMaterialTransferModalOpen, setCancelMaterialTransferModalOpen] = useState<boolean>(false);
    const [acceptMaterialTransferModalOpen, setAcceptMaterialTransferModalOpen] = useState<boolean>(false);

    const [loadingMaterialsReceiving, setLoadingMaterialsReceiving] = useState<boolean>(false);
    const [loadingMaterialsPending, setLoadingMaterialsPending] = useState<boolean>(false);

    // Unlike assets, materials can be transferred by a technician between their own storage locations. Since this is
    // the case, there is no unambiguous way to know whether-or-not the transfer cancellation is being cancelled by a
    // technician or rejected by a technician. Functionally there is no difference, but the text should align with
    // the menu selected. This tells the modal which text to display.
    const [rejection, setRejection] = useState<boolean>(false);

    useEffect(() => {
        updateReceivingMaterials(dispatch, setMaterialsToReceive, setLoadingMaterialsReceiving);
        updatePendingMaterials(dispatch, setMaterialsPending, setLoadingMaterialsPending);
    }, [dispatch]);

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "My Parts/Materials", route: "/materials/tech-transfer" },
                ]}
            />

            <InventoryTechTransferModal
                open={initiateMaterialTransferModalOpen}
                handleClose={() => setInitiateMaterialTransferModalOpen(false)}
                onMaterialTransferRequested={() => {
                    updateReceivingMaterials(dispatch, setMaterialsToReceive, setLoadingMaterialsReceiving);
                    updatePendingMaterials(dispatch, setMaterialsPending, setLoadingMaterialsPending);
                }}
                onSubmitMaterialTransferRequest={(
                    fromInventoryId: number,
                    toInventoryId: number,
                    materialId: number,
                    amount: number,
                    notes: Nullable<string>,
                    modalCallback: Effect
                ) => {
                    createMaterialTechTransfer(
                        fromInventoryId,
                        toInventoryId,
                        materialId,
                        amount,
                        notes,
                        dispatch,
                        modalCallback,
                        () => {}
                    );
                }}
                existingTechnicianEntryEgresses={[]}
            />

            <CancelMaterialTechTransferModal
                materialTechTransfer={selectedMaterialTransfer as MaterialTechTransfer}
                onMaterialTransferCancelled={() => {
                    updateReceivingMaterials(dispatch, setMaterialsToReceive, setLoadingMaterialsReceiving);
                    updatePendingMaterials(dispatch, setMaterialsPending, setLoadingMaterialsPending);
                }}
                rejection={rejection}
                open={cancelMaterialTransferModalOpen}
                handleClose={() => setCancelMaterialTransferModalOpen(false)}
            />

            <AcceptMaterialTechTransferModal
                materialTechTransfer={selectedMaterialTransfer as MaterialTechTransfer}
                onMaterialTransferAccepted={() => {
                    updateReceivingMaterials(dispatch, setMaterialsToReceive, setLoadingMaterialsReceiving);
                    updatePendingMaterials(dispatch, setMaterialsPending, setLoadingMaterialsPending);
                }}
                open={acceptMaterialTransferModalOpen}
                handleClose={() => setAcceptMaterialTransferModalOpen(false)}
            />

            {(loadingMaterialsReceiving || loadingMaterialsPending) && (
                <Box width="100%" display="flex" flexDirection="row" justifyContent="center" marginTop="30%">
                    <CircularProgress size="4rem" />
                </Box>
            )}

            {!loadingMaterialsReceiving && !loadingMaterialsPending && (
                <Box>
                    <Container sx={{ width: "100%", padding: "0px" }}>
                        <Divider>
                            <Typography variant="h6" align="center">
                                Materials to Receive
                            </Typography>
                        </Divider>
                        <Box sx={{ mt: 3 }}>
                            {materialsToReceive.length === 0 && (
                                <Typography align="center" color="info.main" sx={{ mb: 4 }}>
                                    You have no materials to receive.
                                </Typography>
                            )}
                            {materialsToReceive.map((materialTransfer: MaterialTechTransfer, key: number) => {
                                return (
                                    <Card key={key} sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="h6">{materialTransfer.material.name}</Typography>
                                                <Box>
                                                    <Tooltip title="Reject Material">
                                                        <IconButton
                                                            disabled
                                                            sx={{ display: "none" }}
                                                            onClick={() => {
                                                                setRejection(true);
                                                                setSelectedMaterialTransfer(materialTransfer);
                                                                setCancelMaterialTransferModalOpen(true);
                                                            }}
                                                        >
                                                            <Close color="warning" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Confirm Material Receipt">
                                                        <Button
                                                            sx={{ mb: 2 }}
                                                            variant="contained"
                                                            size="small"
                                                            color="success"
                                                            onClick={() => {
                                                                setSelectedMaterialTransfer(materialTransfer);
                                                                setAcceptMaterialTransferModalOpen(true);
                                                            }}
                                                        >
                                                            <Check fontSize="small" />
                                                            &nbsp;Confirm
                                                        </Button>
                                                    </Tooltip>
                                                </Box>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography>{materialTransfer.material.manufacturer}</Typography>
                                                {!mobile && (
                                                    <Typography component="span" color="info.main">
                                                        {
                                                            materialTransfer.from_inventory.storage_location.responsible_user
                                                                ?.first_name
                                                        }
                                                        &nbsp;
                                                        {
                                                            materialTransfer.from_inventory.storage_location.responsible_user
                                                                ?.last_name
                                                        }
                                                        &nbsp;
                                                        <ArrowForward fontSize="small" sx={{ mb: -0.65 }} color="info" />
                                                        &nbsp;
                                                        {
                                                            materialTransfer.to_inventory.storage_location.responsible_user
                                                                ?.first_name
                                                        }
                                                        &nbsp;
                                                        {
                                                            materialTransfer.to_inventory.storage_location.responsible_user
                                                                ?.last_name
                                                        }
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Grid direction="row" justifyContent="space-between" container>
                                                <Grid xs={mobile ? 12 : 8} item>
                                                    <Typography variant="subtitle2">
                                                        Mfr.: <b>{materialTransfer.material.manufacturer ?? "--"}</b>
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        P/N: <b>{materialTransfer.material.part_number}</b>
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        Amt: <b>{materialTransfer.amount}</b>
                                                    </Typography>
                                                </Grid>
                                                {!mobile && (
                                                    <Grid xs={4} item sx={{ width: "100%" }}>
                                                        <Typography variant="subtitle2" color="info.main" align="right">
                                                            Waiting for you to receive
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>

                                            {mobile && <Box sx={{ mb: 3 }} />}

                                            {mobile && (
                                                <Stack direction="row" justifyContent="flex-start">
                                                    <Typography variant="body2" color="info.main">
                                                        {
                                                            materialTransfer.from_inventory.storage_location.responsible_user
                                                                ?.first_name
                                                        }
                                                        &nbsp;
                                                        {
                                                            materialTransfer.from_inventory.storage_location.responsible_user
                                                                ?.last_name
                                                        }
                                                        &nbsp;
                                                    </Typography>
                                                    <ArrowForward fontSize="small" sx={{ mb: -3 }} color="info" />
                                                    <Typography variant="body2" color="info.main">
                                                        &nbsp;
                                                        {
                                                            materialTransfer.to_inventory.storage_location.responsible_user
                                                                ?.first_name
                                                        }
                                                        &nbsp;
                                                        {
                                                            materialTransfer.to_inventory.storage_location.responsible_user
                                                                ?.last_name
                                                        }
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {mobile && (
                                                <Typography variant="caption" color="info.main">
                                                    Waiting for you to receive.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                    </Container>

                    <Container sx={{ width: "100%", padding: "0px" }}>
                        <Divider>
                            <Typography variant="h6" align="center">
                                Pending Material Transfers
                            </Typography>
                        </Divider>

                        <Box sx={{ mt: 3 }}>
                            {materialsPending.length === 0 && (
                                <Typography align="center" color="info.main" sx={{ mb: 1 }}>
                                    There are no pending Material Transfers.
                                </Typography>
                            )}

                            {materialsPending.map((materialTransfer: MaterialTechTransfer, key: number) => {
                                return (
                                    <Card key={key} sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="h6">{materialTransfer.material.name}</Typography>
                                                <Box>
                                                    <Tooltip title="Cancel Material Transfer">
                                                        <Button
                                                            sx={{ mb: 2 }}
                                                            variant="contained"
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => {
                                                                setRejection(false);
                                                                setSelectedMaterialTransfer(materialTransfer);
                                                                setCancelMaterialTransferModalOpen(true);
                                                            }}
                                                        >
                                                            <CancelScheduleSend fontSize="small" />
                                                            &nbsp;Cancel
                                                        </Button>
                                                    </Tooltip>
                                                </Box>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography>{materialTransfer.material.manufacturer}</Typography>

                                                {!mobile && (
                                                    <Stack>
                                                        <Typography component="span" color="info.main">
                                                            {
                                                                materialTransfer.from_inventory.storage_location.responsible_user
                                                                    ?.first_name
                                                            }
                                                            &nbsp;
                                                            {
                                                                materialTransfer.from_inventory.storage_location.responsible_user
                                                                    ?.last_name
                                                            }
                                                            &nbsp;
                                                            <ArrowForward fontSize="small" sx={{ mb: -0.65 }} color="info" />
                                                            &nbsp;
                                                            {
                                                                materialTransfer.to_inventory.storage_location.responsible_user
                                                                    ?.first_name
                                                            }
                                                            &nbsp;
                                                            {
                                                                materialTransfer.to_inventory.storage_location.responsible_user
                                                                    ?.last_name
                                                            }
                                                        </Typography>
                                                        <Typography component="span" color="info.main">
                                                            {materialTransfer.from_inventory.storage_location.name}&nbsp;
                                                            <ArrowForward fontSize="small" sx={{ mb: -0.65 }} color="info" />
                                                            &nbsp;
                                                            {materialTransfer.to_inventory.storage_location.name}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </Stack>
                                            <Grid direction="row" justifyContent="space-between" container>
                                                <Grid xs={mobile ? 12 : 8} item>
                                                    <Typography variant="subtitle2">
                                                        Mfr: <b>{materialTransfer.material.manufacturer ?? "--"}</b>
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        P/N: <b>{materialTransfer.material.part_number}</b>
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        Amt: <b>{materialTransfer.amount}</b>
                                                    </Typography>
                                                </Grid>

                                                {!mobile && (
                                                    <Grid xs={4} item>
                                                        <Typography variant="subtitle2" align="right" color="info.main">
                                                            Waiting to be received by&nbsp;
                                                            {
                                                                materialTransfer.to_inventory.storage_location.responsible_user
                                                                    ?.first_name
                                                            }
                                                            &nbsp;
                                                            {
                                                                materialTransfer.to_inventory.storage_location.responsible_user
                                                                    ?.last_name
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>

                                            {mobile && <Box sx={{ mb: 3 }} />}

                                            {mobile && (
                                                <Box>
                                                    <Stack direction="row" justifyContent="flex-start">
                                                        <Typography variant="body2" color="info.main">
                                                            {
                                                                materialTransfer.from_inventory.storage_location.responsible_user
                                                                    ?.first_name
                                                            }
                                                            &nbsp;
                                                            {
                                                                materialTransfer.from_inventory.storage_location.responsible_user
                                                                    ?.last_name
                                                            }
                                                            &nbsp;
                                                        </Typography>
                                                        <ArrowForward fontSize="small" sx={{ mb: -3 }} color="info" />
                                                        <Typography variant="body2" color="info.main">
                                                            &nbsp;
                                                            {
                                                                materialTransfer.to_inventory.storage_location.responsible_user
                                                                    ?.first_name
                                                            }
                                                            &nbsp;
                                                            {
                                                                materialTransfer.to_inventory.storage_location.responsible_user
                                                                    ?.last_name
                                                            }
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" justifyContent="flex-start">
                                                        <Typography variant="body2" color="info.main">
                                                            {materialTransfer.from_inventory.storage_location.name}&nbsp;
                                                        </Typography>
                                                        <ArrowForward fontSize="small" sx={{ mb: -3 }} color="info" />
                                                        <Typography variant="body2" color="info.main">
                                                            &nbsp;
                                                            {materialTransfer.to_inventory.storage_location.name}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            )}

                                            {mobile && (
                                                <Typography variant="caption" color="info.main">
                                                    Waiting to be received by&nbsp;
                                                    {materialTransfer.to_inventory.storage_location.responsible_user?.first_name}
                                                    &nbsp;
                                                    {materialTransfer.to_inventory.storage_location.responsible_user?.last_name}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                    </Container>
                </Box>
            )}

            {!loadingMaterialsReceiving && !loadingMaterialsPending && (
                <Box sx={{ mt: 5, ml: mobile ? 0 : 3 }}>
                    <Button variant="contained" onClick={() => setInitiateMaterialTransferModalOpen(true)}>
                        <Send />
                        &nbsp;Initiate Material Transfer
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default MaterialsTechTransfers;
